import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Upload,
  Building2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [bomFile, setBomFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should not exceed 10MB');
        return;
      }
      setBomFile(file);
      toast.success(`File "${file.name}" selected`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Frontend Validation
    if (formData.name.length < 2) {
      toast.error('Name must be at least 2 characters');
      setIsSubmitting(false);
      return;
    }
    
    if (formData.name.length > 100) {
      toast.error('Name must not exceed 100 characters');
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    if (formData.phone.length < 10 || formData.phone.length > 20) {
      toast.error('Phone must be between 10 and 20 characters');
      setIsSubmitting(false);
      return;
    }

    if (formData.subject.length < 5) {
      toast.error('Subject must be at least 5 characters');
      setIsSubmitting(false);
      return;
    }

    if (formData.subject.length > 200) {
      toast.error('Subject must not exceed 200 characters');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length < 10) {
      toast.error('Message must be at least 10 characters');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 2000) {
      toast.error('Message must not exceed 2000 characters');
      setIsSubmitting(false);
      return;
    }

    try {
      // Create FormData for multipart/form-data submission
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('company', formData.company || '');
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      
      if (bomFile) {
        formDataToSend.append('bom_file', bomFile);
      }

      // Submit to backend
      const response = await axios.post(
        `${BACKEND_URL}/api/inquiry/submit`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
        setBomFile(null);
        
        // Reset file input
        const fileInput = document.getElementById('bom');
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('Submission error:', error);
      
      // Show error message with fallback instructions
      const errorMessage = (
        <div className="space-y-2">
          <p>⚠️ Unable to process your inquiry through our system.</p>
          <p className="font-semibold">Please send your inquiry directly to:</p>
          <p className="text-lg font-bold text-blue-600">sales@stechnoproducts.in</p>
          <p className="text-sm">Include your name, phone, and inquiry details in your email.</p>
        </div>
      );
      
      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="page-header">
        <div className="container mx-auto px-6 py-16">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Get in touch with us for quotes, inquiries, or technical support
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="contact-info-card">
              <CardHeader>
                <CardTitle>Get In Touch</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="contact-label">Email</p>
                    <a href="mailto:sales@stechnoproducts.in" className="contact-value">
                      sales@stechnoproducts.in
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="contact-label">Phone / WhatsApp</p>
                    <a href="tel:+919535217376" className="contact-value">
                      +91 95352 17376
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="contact-label">Address</p>
                    <p className="contact-value">
                      No. 53/2, 4th cross, Kaveripura,<br />
                      Kamakshipalya<br />
                      Bengaluru, Karnataka - 560079
                    </p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="contact-label">GSTIN</p>
                    <p className="contact-value">29JDFPS9009J1ZR</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="business-hours-card">
              <CardHeader>
                <CardTitle>Business Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Monday - Friday</span>
                    <span className="text-gray-600">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Saturday</span>
                    <span className="text-gray-600">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Sunday</span>
                    <span className="text-gray-600">Closed</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="contact-form-card">
              <CardHeader>
                <CardTitle>Send Us a Message</CardTitle>
                <p className="text-gray-600 mt-2">
                  Fill out the form below and we'll get back to you within 24 hours
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 XXXXX XXXXX"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="What is this regarding?"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Please provide details about your inquiry or requirements..."
                      rows={6}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bom">Upload BOM (Optional)</Label>
                    <div className="bom-upload">
                      <Input
                        id="bom"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.xlsx,.xls,.csv"
                        className="hidden"
                      />
                      <label htmlFor="bom" className="bom-upload-label">
                        <Upload className="h-5 w-5" />
                        <span>
                          {bomFile ? bomFile.name : 'Choose file (PDF, Excel, CSV - Max 10MB)'}
                        </span>
                      </label>
                    </div>
                    <p className="text-sm text-gray-500">
                      Upload your Bill of Materials for faster quote processing
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
